import { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import InputField from '@/components/InputField';
import ModalContainer from '@/components/modals/ModalContainer';
import { useGetAllUserQuery } from '@/api/commonApis';
import { replaceUnderLine } from '@/utils/Util';
import { useProductActionAddMutation } from '@/features/Scan/api/scanApi';
import CustomDropdown from '@/components/CustomDropdown';
import { TASK_TYPE } from '@/features/Scan/constants';
import { toastError, toastSuccess } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import CommonImage from '@/components/CommonImage';
const INITIAL_STATE = {
	taskName: '',
	taskType: {},
	selectedUsers: []
};

const CreateNewTask = ({ onClose, onOpenTaskModal, taskInputValueFromTaskModal }) => {
	const [taskInputValue, setTaskInputValue] = useState(INITIAL_STATE);
	const [errors, setErrors] = useState(null);

	const { data: users } = useGetAllUserQuery({ params: { page: 0, size: 1000 } });
	const [productActionAdd, { isLoading }] = useProductActionAddMutation();

	useEffect(() => {
		if (taskInputValueFromTaskModal) {
			setTaskInputValue((prev) => ({
				...prev,
				taskName: taskInputValueFromTaskModal
			}));
		}
	}, [taskInputValueFromTaskModal]);

	const getFormattedUsers = () => {
		if (!users) return [];
		const uniqueUserTypes = [...new Set(users.map((user) => user.primaryUserType))];

		return uniqueUserTypes.map((userType) => ({
			id: users.find((user) => user.primaryUserType === userType).id,
			value: userType,
			label: replaceUnderLine(userType)
		}));
	};

	const handleChange = (value, fieldName) => {
		setTaskInputValue((prev) => ({
			...prev,
			[fieldName]: value
		}));
		setErrors((prev) => ({
			...prev,
			[fieldName]: ''
		}));
	};

	const isValidTask = () => {
		const newErrors = {};
		if (!taskInputValue?.taskName) {
			newErrors.taskName = 'Task name is required.';
		}
		if (taskInputValue.selectedUsers?.length === 0) {
			newErrors.selectedUsers = 'Select at least one User';
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!isValidTask()) {
			return;
		}

		const requestBody = {
			name: taskInputValue?.taskName,
			taskValueType: taskInputValue?.taskType?.taskType,
			userTypeList: taskInputValue?.selectedUsers?.map((user) => user?.value)
		};

		productActionAdd({ body: requestBody })
			.unwrap()
			.then(() => {
				toastSuccess('Task created successfully');
				onClose(taskInputValue?.taskName);
				onOpenTaskModal();
			})
			.catch((error) => {
				toastError(error?.data?.message);
			});
	};

	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer className="update-sample-and-sales-modal">
				<div className="popup-wrapper width-420-modal">
					<div className="common-popup update-sample-and-sales-modal">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-14 mb-0">Add task</h3>
							</div>
							<div className="close-btn" onClick={onClose}>
								<CommonImage src="/icons/close.svg" alt="close" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="row">
								<div className="col-md-12 pr-2">
									<InputField
										type="text"
										value={taskInputValue?.taskName}
										onChange={(e) => handleChange(e.target.value, 'taskName')}
										name="taskName"
										withLabel
										labelTitle="Task name"
										errors={errors?.taskName}
									/>
								</div>
								<div className="col-md-12 pr-2">
									<div className="form-group">
										<label htmlFor="task">Select Task Type</label>
										<CustomDropdown
											items={TASK_TYPE}
											title="Select task"
											selectedItem={taskInputValue?.taskType?.name}
											onItemClick={(item) => handleChange(item, 'taskType')}
										/>
										{errors?.task && <span className="error">{errors?.task}</span>}
									</div>
								</div>
								<div className="col-md-12 pr-2">
									<div className="form-group">
										<div className="form-group no-shadow-react-select">
											<label htmlFor="collection">Who will be able to update?</label>
											<CreatableSelect
												value={taskInputValue?.selectedUsers}
												isMulti
												onChange={(selected) =>
													handleChange(selected, 'selectedUsers')
												}
												options={getFormattedUsers()}
												isClearable
												isValidNewOption={() => false}
											/>
											<span className="error">{errors?.selectedUsers}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button className="button outline size32 ml-2" onClick={onClose}>
									Cancel
								</button>
								<button className="button size32 ml-2" onClick={handleSubmit}>
									Update
								</button>
							</div>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default CreateNewTask;
